import { useAuth0 } from "@auth0/auth0-react";
import { type Mutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import type {
  AccountListKeys,
  AccountsController,
  AccountWithHoldingsAndBalances,
} from "../../../../api/src/accounts/accounts.controller";
import type {
  Account,
  AccountMetrics,
  AccountWithHoldingsAndRiskAllocation,
  AccountWithMetrics,
} from "../../../../api/src/accounts/accounts.service";
import type { CurrentAndTargetRiskAllocation } from "../../../../api/src/households/households.service";
import type {
  PaginationResponseConfig,
  SerializedObject,
  UnpackResponse,
} from "../../../../api/src/lib";
import {
  authenticatedFetch,
  deserializeDate,
  useAuthenticatedMutationNew,
  useAuthenticatedQuery,
} from "../../lib/api";
import { processFileResponse } from "../../lib/file";
import {
  CONSOLE_PAGE_SIZE,
  useAuthenticatedFetchAllPages,
} from "../../lib/pagination";
import {
  deserializeAccountPercents,
  deserializeHoldingPercents,
} from "../percents";

/**
 * `GET /accounts` (and `POST /accounts/filter-by-ids`) send `AccountWithMetrics`
 * with `balances=true` and `Account` without it.
 */
type AccountsWithBalances<TBalances extends boolean> = TBalances extends true
  ? AccountWithMetrics
  : Account;

/**
 * The RQL field each Accounts table column filters and sorts on
 * (`api/src/accounts/accounts.rql.ts`). The Name column shows `displayName`,
 * which is the account's `name` column (`mapDbAccountToAccount`), so it is
 * sent as `name`, a database field, and runs in SQL.
 */
const ACCOUNT_RQL_FIELD_OF_COLUMN: Record<string, string> = {
  displayName: "name",
  householdName: "householdName",
  type: "type",
  model: "model",
  isActive: "isActive",
  accountBalance: "accountBalance",
  cashBalance: "cashBalance",
  cashBalanceWeight: "cashBalanceWeight",
  riskAllocation: "riskAllocation",
  inceptionDate: "inceptionDate",
};

/** `value` as an RQL string, quoted, with `"` and `\` escaped. */
const rqlString = (value: unknown) =>
  `"${String(value).replace(/[\\"]/g, (char) => `\\${char}`)}"`;

/**
 * The RQL `q` and `sort` of `GET /accounts` for the Accounts table's column
 * filters and sorting (APP-1653). Each filter is one term, joined by `and`:
 * a text filter matches as a case-insensitive contains, as it did before, and
 * the Status filter (`StatusFilter`, a boolean) is `isActive = "true"` or
 * `"false"`. The sort always names its direction: the server sorts a bare
 * field ascending.
 */
export function accountTableRql(
  filters: ColumnFiltersState,
  sorting: SortingState,
): { q?: string; sort?: string } {
  const terms = filters.flatMap(({ id, value }) => {
    const field = ACCOUNT_RQL_FIELD_OF_COLUMN[id];
    if (
      field === undefined ||
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return [];
    }
    return [
      `${field} = ${rqlString(field === "isActive" ? String(value) === "true" : value)}`,
    ];
  });
  const [firstSort] = sorting;
  const sortField =
    firstSort === undefined
      ? undefined
      : ACCOUNT_RQL_FIELD_OF_COLUMN[firstSort.id];

  return {
    ...(terms.length === 0 ? {} : { q: terms.join(" and ") }),
    ...(sortField === undefined
      ? {}
      : { sort: `${sortField}:${firstSort.desc ? "desc" : "asc"}` }),
  };
}

/**
 * One page of the Accounts table. `GET /accounts` answers under `limit` and
 * `offset` with RQL `q` and `sort` (APP-1653); `POST /accounts/filter-by-ids`,
 * used when there are ids, still takes `pageNumber`, `pageSize`, `filters` and
 * `sorting` and answers with `config`. `config` is the same on both: the table
 * reads `totalCount` and `pageCount` from it.
 *
 * The query key is unchanged, so `invalidateQueries({ queryKey: ["accounts"] })`
 * still reaches it. A screen that needs every account reads
 * `useQueryAllAccounts`.
 */
export function useQueryAccounts<TBalances extends boolean = false>(
  {
    includeBalances = false as TBalances,
    includeInactive = false,
    filters = [] as ColumnFiltersState,
    sorting = [] as SortingState,

    pageSize = 10,
    pageIndex = 0, // front-end pageIndex is 0 based vs backend pageNumber which is 1 based index
    page = 1,
    enabled = true,
  }: {
    includeBalances?: TBalances;
    includeInactive?: boolean;
    filters?: ColumnFiltersState;
    sorting?: SortingState;
    pageSize?: number;
    pageIndex?: number;
    page?: number;
    enabled?: boolean;
  } = {},
  accountIds?: number[],
) {
  return useAuthenticatedQuery<
    | UnpackResponse<AccountsController["getAllAccounts"]>
    | UnpackResponse<AccountsController["getAccountsByIds"]>,
    {
      data: AccountsWithBalances<TBalances>[];
      config: PaginationResponseConfig;
      page: number;
    },
    [
      string,
      {
        includeBalances: boolean;
        includeInactive: boolean;
        filters: ColumnFiltersState;
        sorting: SortingState;
        pageSize: number;
        pageIndex: number;
        accountIds?: number[];
      },
      number,
    ]
  >({
    queryKey: [
      "accounts",
      {
        includeBalances,
        includeInactive,
        filters,
        sorting,
        pageSize,
        pageIndex,
        accountIds,
      },
      page,
    ],
    queryFn: async (accounts) => {
      const totalCount =
        "pagination" in accounts
          ? accounts.pagination.totalCount
          : (accounts.config?.totalCount ?? 0);
      return {
        // With `balances=true` the percents come on 0 to 100.
        data: accounts.data.map(
          (account) =>
            deserializeAccountWithMetrics(
              deserializeAccountPercents(account),
            ) as AccountsWithBalances<TBalances>,
        ),
        config:
          "pagination" in accounts
            ? {
                pageIndex,
                pageSize,
                totalCount,
                pageCount: Math.ceil(totalCount / pageSize),
              }
            : {
                ...accounts?.config,
                pageIndex: accounts?.config.pageIndex ?? 0,
                pageSize: accounts?.config.pageSize ?? 0,
              },
        page,
      };
    },
    urlBuilderFn: (key) => {
      // With a (potentially large) id list, POST the ids in a body to avoid
      // the GET query-string overflowing into a 414 (APP-1277).
      if ((key[1].accountIds ?? []).length > 0) {
        return "/accounts/filter-by-ids";
      }

      // `GET /accounts` is served at `/v1` only (APP-1653); the key keeps
      // `"accounts"` first, so `invalidateQueries({ queryKey: ["accounts"] })`
      // still reaches it. `POST /accounts/filter-by-ids` above is excluded and
      // stays unversioned.
      const { q, sort } = accountTableRql(key[1].filters, key[1].sorting);
      return `/v1/${key[0]}?${new URLSearchParams({
        balances: String(key[1].includeBalances),
        includeInactive: String(key[1].includeInactive),
        limit: String(key[1].pageSize),
        offset: String(key[1].pageIndex * key[1].pageSize),
        ...(q === undefined ? {} : { q }),
        ...(sort === undefined ? {} : { sort }),
      }).toString()}`;
    },
    fetchOptions:
      (accountIds ?? []).length > 0
        ? {
            method: "POST",
            body: {
              accountIds,
              balances: includeBalances,
              includeInactive,
              pageNumber: pageIndex + 1,
              pageSize,
              filters: JSON.stringify(filters ?? []),
              sorting: JSON.stringify(sorting ?? []),
            },
          }
        : undefined,
    queryOptions: { enabled },
  });
}

/**
 * The ids, as row ids (`String(account.id)`), of every account the Accounts
 * table's filters match, on every page (R30 and R33 of
 * `doc-account-list-01a15430ceb643e0`). The header tick box selects, clears
 * and shows these (APP-1819).
 *
 * Without `accountIds` this reads `GET /v1/accounts` with the `q` of
 * `filters`, `balances=false`, `includeInactive=true` and no `sort`. With
 * `accountIds` (the ids of an `?insight=` or `?id=` link) it reads
 * `POST /accounts/filter-by-ids` with those ids in the body, so each matching
 * account is one of them and the url stays short (APP-1277). Either way it
 * asks for `totalCount` rows a page (the table's total, so one page is
 * usually enough) and reads page after page until it holds the total the
 * response names.
 *
 * The key starts with `"accounts"`, so `invalidateQueries({ queryKey:
 * ["accounts"] })` reaches it. `enabled: false` sends nothing; `fetchIds`
 * reads the ids on demand (a tick) through the same key, and rejects when the
 * read fails.
 */
export function useQueryMatchingAccountIds({
  filters,
  accountIds,
  totalCount,
  enabled,
}: {
  filters: ColumnFiltersState;
  accountIds?: number[];
  totalCount: number;
  enabled: boolean;
}) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => ["accounts", { matchingIds: true, filters, accountIds }] as const,
    [filters, accountIds],
  );

  const queryFn = useCallback(async () => {
    const apiRoot = process.env.REACT_APP_API_ROOT;
    const pageSize = totalCount > 0 ? totalCount : CONSOLE_PAGE_SIZE;
    const ids: string[] = [];

    if ((accountIds ?? []).length > 0) {
      for (let pageNumber = 1; ; pageNumber++) {
        const page = await authenticatedFetch<
          SerializedObject<
            UnpackResponse<AccountsController["getAccountsByIds"]>
          >
        >(`${apiRoot}/accounts/filter-by-ids`, getAccessTokenSilently, {
          method: "POST",
          body: {
            accountIds,
            balances: false,
            includeInactive: true,
            pageNumber,
            pageSize,
            filters: JSON.stringify(filters),
          },
        })();
        ids.push(...page.data.map((account) => String(account.id)));
        if (
          page.data.length === 0 ||
          ids.length >= (page.config?.totalCount ?? 0)
        ) {
          return ids;
        }
      }
    }

    const { q } = accountTableRql(filters, []);
    let limit = pageSize;
    for (;;) {
      const page = await authenticatedFetch<
        SerializedObject<UnpackResponse<AccountsController["getAllAccounts"]>>
      >(
        `${apiRoot}${ACCOUNTS_PATH}?${new URLSearchParams({
          balances: "false",
          includeInactive: "true",
          limit: String(limit),
          offset: String(ids.length),
          ...(q === undefined ? {} : { q }),
        }).toString()}`,
        getAccessTokenSilently,
      )();
      ids.push(...page.data.map((account) => String(account.id)));
      if (page.data.length === 0 || ids.length >= page.pagination.totalCount) {
        return ids;
      }
      // A server that answered with fewer rows than were asked for is
      // followed on the size it named, as `useAuthenticatedFetchAllPages` does.
      limit = page.pagination.limit;
    }
  }, [accountIds, filters, getAccessTokenSilently, totalCount]);

  // The ids a tick has just read (`fetchIds`) are not read again when the
  // tick enables this; a change of the filters or an invalidation reads them
  // again. `totalCount` sizes the pages; it does not change which ids match,
  // so it is not in the key.
  const query = useQuery({ queryKey, queryFn, enabled, staleTime: Infinity });

  const fetchIds = useCallback(
    () => queryClient.fetchQuery({ queryKey, queryFn }),
    [queryClient, queryKey, queryFn],
  );

  return { data: query.data, isFetching: query.isFetching, fetchIds };
}

/** The path of `getAllAccounts`, and the first element of every key of the page walk in `useQueryAllAccounts`. */
export const ACCOUNTS_PATH = "/v1/accounts";

/** One item of `GET /accounts`, as the route serialises it. */
type SerializedAccountListItem = SerializedObject<
  UnpackResponse<AccountsController["getAllAccounts"]>
>["data"][number];

/**
 * True for a create, update or delete under `/accounts`: every mutation key
 * whose method is `POST`, `PUT` or `DELETE` and whose first path element is
 * `"accounts"` (`useUpdateAccountsModel`, among others).
 */
const isAccountWrite = (mutation: Mutation<unknown, unknown, unknown>) => {
  const [method, root] = mutation.options.mutationKey ?? [];
  return (
    ["POST", "PUT", "DELETE"].includes(String(method)) && root === "accounts"
  );
};

/**
 * Every account the user can see. `GET /accounts` answers a page (APP-1653),
 * and every screen that reads this hook shows, picks from or looks up the whole
 * list (the billing, cash, insight, rebalance and report screens), so this
 * walks every page (`useAuthenticatedFetchAllPages`) and holds `data` back
 * until the last one is in, as `useQueryHouseholds`
 * (`clients/household/lib.ts`) does. Nothing is filtered or sorted in the
 * browser.
 *
 * When a write under `/accounts` succeeds, the cached pages are dropped and the
 * walk starts again; `reload` does the same for a screen whose write no
 * mutation cache records (`useAuthenticatedMutationAsync`). When the last
 * reader unmounts, the cached pages are dropped too, so a reader mounted later
 * walks the list again.
 */
export function useQueryAllAccounts<TBalances extends boolean = false>({
  includeBalances = false as TBalances,
  includeInactive = false,
  enabled = true,
}: {
  includeBalances?: TBalances;
  includeInactive?: boolean;
  enabled?: boolean;
} = {}) {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedAccountListItem>(
      ACCOUNTS_PATH,
      {
        balances: String(includeBalances),
        includeInactive: String(includeInactive),
      },
      { enabled },
    );

  const restart = useCallback(() => {
    queryClient.removeQueries({ queryKey: [ACCOUNTS_PATH] });
    reload();
  }, [queryClient, reload]);

  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isAccountWrite(event.mutation)
        ) {
          restart();
        }
      }),
    [queryClient, restart],
  );

  // On unmount, drop the pages no other mounted reader still observes. This
  // cleanup runs after the walk's own observer has unsubscribed, because the
  // walk's hooks are called above it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [ACCOUNTS_PATH],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient],
  );

  // Each item of `GET /accounts` also carries `householdName`, `modelName`
  // and `householdLatestRebalance` (`AccountListKeys`).
  const data:
    | { data: (AccountsWithBalances<TBalances> & AccountListKeys)[] }
    | undefined = useMemo(
    () =>
      hasEveryRow
        ? {
            // With `balances=true` the percents come on 0 to 100.
            data: rows.map(
              (account) =>
                deserializeAccountWithMetrics(
                  deserializeAccountPercents(account),
                ) as AccountsWithBalances<TBalances> & AccountListKeys,
            ),
          }
        : undefined,
    [rows, hasEveryRow],
  );

  const isPending = !hasEveryRow && !isError;

  return {
    isPending,
    isLoading: isPending && enabled,
    isError,
    data,
    reload: restart,
  };
}

/**
 * The path of `getAccountHoldings` for one account. Also the FIRST element of
 * every react-query key the page walk in `useQueryAccountHoldings` uses, so
 * `[accountHoldingsPath(id)]` reaches every page of that walk.
 */
export const accountHoldingsPath = (id: number) =>
  `/v1/accounts/${id}/holdings`;

/** One item of `GET /accounts/:id/holdings`, as the route serialises it. */
type SerializedAccountHolding = SerializedObject<
  UnpackResponse<AccountsController["getAccountHoldings"]>
>["data"][number];

/**
 * One `AccountHoldingResponse` per security of the account, over the period
 * `periodParams` names (the `start-date` and `end-date` query string the
 * period selectors build, or `""`). `GET /accounts/:id/holdings` answers a
 * page in `securityId` order (APP-1653), and its reader (`AccountTaxTable.tsx`)
 * shows every holding, so this walks every page
 * (`useAuthenticatedFetchAllPages`) and holds `data` back until the last one
 * is in. Each holding's `security.riskAllocation` goes through
 * `deserializeHoldingPercents`, as before.
 *
 * As `useQueryHouseholdPerformance` (`clients/household/lib.ts`) does: when
 * the last reader unmounts, the cached pages are dropped; and when `id` or
 * `periodParams` changes while the reader stays mounted, the walk starts again
 * and `data` stays `undefined` until it is in.
 *
 * An `id` of `undefined` (no account in the route) or `enabled: false` asks
 * for nothing.
 */
export function useQueryAccountHoldings(
  id: number | undefined,
  periodParams: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const path = accountHoldingsPath(id ?? 0);
  const query = useMemo(
    () => Object.fromEntries(new URLSearchParams(periodParams)),
    [periodParams],
  );

  // The account and period whose holdings the walk below holds. It trails
  // them by one render when either changes; the walk is held and `data`
  // withheld meanwhile.
  const walkKey = `${id}?${periodParams}`;
  const [walkedKey, setWalkedKey] = useState(walkKey);
  const isWalkOfKey = walkedKey === walkKey;

  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedAccountHolding>(path, query, {
      enabled: enabled && id !== undefined && isWalkOfKey,
    });

  useEffect(() => {
    if (!isWalkOfKey) {
      reload();
      setWalkedKey(walkKey);
    }
  }, [walkKey, isWalkOfKey, reload]);

  // On unmount, or when the account changes, drop the pages of the path no
  // other mounted reader still observes. This cleanup runs after the walk's
  // own observer has unsubscribed, because the walk's hooks are called above
  // it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [path],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient, path],
  );

  const data = useMemo(
    () =>
      hasEveryRow && isWalkOfKey
        ? rows.map(deserializeHoldingPercents)
        : undefined,
    [rows, hasEveryRow, isWalkOfKey],
  );

  return { isPending: data === undefined && !isError, isError, data };
}

/**
 * `GET /accounts/:id` sends `AccountWithHoldingsAndBalances` with
 * `balances=true` and `AccountWithHoldingsAndRiskAllocation` without it.
 */
type AccountWithBalances<TBalances extends boolean> = TBalances extends true
  ? AccountWithHoldingsAndBalances
  : AccountWithHoldingsAndRiskAllocation;

export function useQueryAccount<TBalances extends boolean = false>(
  id: number,
  {
    includeBalances = false as TBalances,
    enabled = true,
  }: { includeBalances?: TBalances; enabled?: boolean } = {},
) {
  return useAuthenticatedQuery<
    UnpackResponse<AccountsController["getAccount"]>,
    AccountWithBalances<TBalances> | undefined,
    [
      string,
      number,
      {
        includeBalances: boolean;
      },
    ]
  >({
    queryKey: ["accounts", id, { includeBalances }],
    queryFn: async (account) =>
      typeof account?.data === "undefined"
        ? undefined
        : (deserializeAccountWithMetrics(
            // `holdings[].security.riskAllocation` and `riskAllocation` come
            // on 0 to 100.
            deserializeAccountPercents(account.data),
          ) as AccountWithBalances<TBalances>),
    urlBuilderFn: (key) =>
      `/v1/${key[0]}/${key[1]}/?balances=${key[2].includeBalances}`,
    queryOptions: { enabled },
  });
}

export function useUpdateAccountsModel() {
  return useAuthenticatedMutationNew<
    UnpackResponse<AccountsController["bulkAssignModelToAccounts"]>,
    {
      accountId: number;
      modelId: number;
    }[],
    {
      modelId: number;
      accounts: Record<number, boolean>;
    }
  >({
    // `PUT /v1/accounts/model`, which was `PUT /accounts/model/assign`
    // (APP-1653). The key keeps `"accounts"` first, so `isAccountWrite`
    // matches it; the url is under `/v1`.
    mutationKey: ["PUT", "accounts", "model"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: ({
      data,
    }: {
      data: {
        accountId: number;
        modelId: number;
      }[];
    }) => data,
  });
}

export function useArchiveAccount(accountId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<AccountsController["archiveAccount"]>,
    void,
    void
  >({
    // `POST /v1/accounts/:id/archive-date`, which was
    // `POST /accounts/:id/archive` (APP-1653). The key keeps `"accounts"`
    // first, so `isAccountWrite` matches it; the url is under `/v1`. The
    // response is the archived account, which this hook does not read.
    mutationKey: ["POST", "accounts", accountId, "archive-date"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: () => {
      queryClient.invalidateQueries({
        queryKey: ["accounts", accountId, { includeBalances: true }],
      });
    },
  });
}

export function useUnarchiveAccount(accountId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<AccountsController["unarchiveAccount"]>,
    void,
    void
  >({
    // `DELETE /v1/accounts/:id/archive-date`, which was
    // `POST /accounts/:id/unarchive` (APP-1653); under `/v1` as
    // `useArchiveAccount` above.
    mutationKey: ["DELETE", "accounts", accountId, "archive-date"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: () => {
      queryClient.invalidateQueries({
        queryKey: ["accounts", accountId, { includeBalances: true }],
      });
    },
  });
}

export function displayAccountStatus(account: Pick<Account, "isActive">) {
  return account.isActive ? "Active" : "Inactive";
}

/**
 * Deserializes any account a route sends. `holdings` is not a key of
 * `Account`: `AccountMetrics`, `AccountWithHoldingsAndRiskAllocation` and
 * `AccountWORiskAllocation` carry it, and a route that sends `Account` alone
 * does not, so an absent `holdings` becomes `[]` here as it always has.
 */
export function deserializeAccountWithMetrics<TAccount extends Account>(
  account: SerializedObject<TAccount>,
) {
  const { holdings } = account as Partial<
    SerializedObject<Pick<AccountMetrics, "holdings">>
  >;

  return {
    ...account,
    billingStartDate: deserializeDate(account.billingStartDate),
    inceptionDate: deserializeDate(account.inceptionDate),
    holdings: (holdings ?? []).map((holding) => ({
      ...holding,
      asOfDate: deserializeDate(holding.asOfDate),
      firstOpenDate: deserializeDate(holding.firstOpenDate),
    })),
    archiveDate: deserializeDate(account.archiveDate),
  } as TAccount;
}

export function deserializeAccountWithMetricsAndAddRiskAllocation<
  TAccount extends AccountWithMetrics,
>(
  account: SerializedObject<TAccount>,
  riskAllocation?: Record<number, CurrentAndTargetRiskAllocation>,
) {
  return {
    ...deserializeAccountWithMetrics(account),
    riskAllocation: riskAllocation ?? account.riskAllocation,
  } as TAccount;
}

export function useQueryExportAccounts() {
  return useAuthenticatedQuery<
    UnpackResponse<AccountsController["exportAccounts"]>,
    void,
    [string, string]
  >({
    queryKey: ["accounts", "export"],
    queryFn: async () => undefined,
    queryOptions: { enabled: false },
    responseCallback: processFileResponse,
  });
}
